import type { Board, Cell, Color, Coord, TileType } from './types.js';
import { coordKey } from './types.js';

/**
 * EXACT glyphs from the original cblix (https://cblix.franzai.com/).
 * Do not change these — they are part of the game's identity.
 *
 * Flipping cells deliberately have NO glyph: in the original cblix the
 * flipping animation itself is the visual indicator (the cell visibly
 * alternates between two colours every 2-4s until it is flooded).
 */
export const GLYPH_FOR_TILE: Record<TileType, string> = {
  normal: '',
  locked: '╳',
  penalty: '•',
  rotateL: '↺',
  rotateR: '↻',
  mirrorH: '⇆',
  mirrorV: '⇵',
  inversion: '∞',
  chaos: '⇅',
  flipping: '',
};

/**
 * Which face a flipping cell currently shows. Phase derives from movesUsed
 * so logic stays pure / deterministic. Phase 0 → primary, 1 → altColor.
 */
export const flippingPhase = (movesUsed: number): 0 | 1 => (movesUsed % 2 === 0 ? 0 : 1);

export const effectiveColor = (cell: Cell, phase: 0 | 1): Color => {
  if (cell.tile !== 'flipping') return cell.color;
  if (phase === 1 && cell.altColor) return cell.altColor;
  return cell.color;
};

export const isSpecial = (t: TileType): boolean =>
  t === 'rotateL' ||
  t === 'rotateR' ||
  t === 'mirrorH' ||
  t === 'mirrorV' ||
  t === 'inversion' ||
  t === 'chaos';

export const buildChaosMapping = (
  palette: readonly Color[],
  pickIdx: (n: number) => number,
): Map<Color, Color> => {
  const shuffled = [...palette];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = pickIdx(i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j] as Color, shuffled[i] as Color];
  }
  const map = new Map<Color, Color>();
  palette.forEach((c, i) => map.set(c, shuffled[i] as Color));
  return map;
};

export const applyMapping = (
  cells: readonly (readonly Cell[])[],
  mapping: ReadonlyMap<Color, Color>,
): readonly (readonly Cell[])[] =>
  cells.map((row) =>
    row.map((cell) => {
      const next = mapping.get(cell.color);
      if (!next || cell.tile === 'locked') return cell;
      return { color: next, tile: cell.tile };
    }),
  );

export const buildInversionMapping = (palette: readonly Color[]): Map<Color, Color> => {
  const reversed = [...palette].reverse();
  const map = new Map<Color, Color>();
  palette.forEach((c, i) => map.set(c, reversed[i] as Color));
  return map;
};

export const applyInversion = applyMapping;

export const triggeredSpecials = (
  board: Board,
  affected: readonly Coord[],
  visited: ReadonlySet<string>,
): { coord: Coord; tile: TileType }[] => {
  const out: { coord: Coord; tile: TileType }[] = [];
  for (const c of affected) {
    const cell = board.cells[c.y]?.[c.x];
    if (!cell) continue;
    if (!isSpecial(cell.tile)) continue;
    if (visited.has(coordKey(c))) continue;
    out.push({ coord: c, tile: cell.tile });
  }
  return out;
};
